// @ts-nocheck
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useHotelPartnerId } from './hooks/useHotelPartnerId';
import { formatCalendarDate } from '@/lib/dates';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRoomBlocks } from './hooks/useRoomBlocks';
import { blockPickupPct } from './roomBlockUtils';

export function useRoomBlockReportTeams(eventIds: string[]) {
  const { data: partnerId } = useHotelPartnerId();
  return useQuery({ queryKey: ['hotel-block-report-teams', eventIds], enabled: eventIds.length > 0 && !!partnerId,
    queryFn: async () => {
      const rows = [];
      for (let offset = 0; ; offset += 1000) {
        const { data, error } = await (supabase as any).from('v_hotel_team_tracker').select('registration_id, team_name')
          .in('event_id', eventIds).order('registration_id').range(offset, offset + 999);
        if (error) throw error;
        rows.push(...(data || []));
        if ((data || []).length < 1000) break;
      }
      return rows;
    },
  });
}

export function RoomBlockPickupReport({ events, eventFilter }: { events: { event_id: string; event_name: string }[]; eventFilter: string }) {
  const ids = events.filter(e => eventFilter === 'all' || e.event_id === eventFilter).map(e => e.event_id);
  const blocks = useRoomBlocks(ids);
  const teams = useRoomBlockReportTeams(ids);
  const rows = (blocks.data || []).map(b => [events.find(e => e.event_id === b.event_id)?.event_name || '',
    b.group_name || '', b.hotel_name, teams.data?.find(t => t.registration_id === b.registration_id)?.team_name || 'unlinked',
    b.rooms_held, b.rooms_reserved, b.room_nights_booked ?? 0, b.room_nights_on_hold ?? 0,
    `${blockPickupPct(b.rooms_held, b.rooms_reserved)}%`, b.release_date || '']);
  const headers = ['Event', 'Group', 'Hotel', 'Team', 'Held', 'Reserved', 'Nights booked', 'Nights on hold', 'Pickup %', 'Release date'];
  function exportCsv() {
    const csv = [headers, ...rows].map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = 'room-block-pickup.csv'; a.click(); URL.revokeObjectURL(url);
  }
  return <section className="space-y-4">
    <div className="flex flex-wrap gap-3 items-center justify-between"><h2 className="text-lg font-semibold">Room Block Pickup</h2>
      <Button variant="outline" className="min-h-11" onClick={exportCsv} disabled={blocks.isLoading || teams.isLoading || !!blocks.error || !!teams.error}>
        <Download className="h-4 w-4 mr-2" />Export CSV</Button></div>
    {(blocks.error || teams.error) && <p role="alert" className="text-destructive">{blocks.error?.message || teams.error?.message}</p>}
    {blocks.isLoading || teams.isLoading ? <p className="text-muted-foreground">Loading room blocks…</p> :
      <div className="overflow-x-auto"><Table><TableHeader><TableRow>{headers.map(h => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{rows.map((row,i) => <TableRow key={blocks.data[i].id}>{row.map((cell,j) =>
          <TableCell key={j} className={j === 9 ? 'whitespace-nowrap' : ''}>{j === 9 ? formatCalendarDate(cell, 'MMM d, yyyy', '—') : cell}</TableCell>)}</TableRow>)}
          {!rows.length && <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">No room blocks.</TableCell></TableRow>}
        </TableBody></Table></div>}
  </section>;
}